import Box from "@mui/material/Box";
import evaluateIconBackdrop from "assets/svg/evaluate-backdrop.svg";
import ActionActuator from "assets/svg/event-action-actuator.svg";
import DelayIcon from "assets/svg/event-action-delay.svg";
import ActionEmail from "assets/svg/event-action-email.svg";
import ActionNotification from "assets/svg/event-action-notification.svg";
import ActionSMS from "assets/svg/event-action-sms.svg";
import HoverAuthorizeTooltip from "components/authorize/AuthorizeTooltip";
import SelectEventVariableModal from "components/events/modals/SelectEventVariableModal";
import DefaultModal from "components/modals/ModalTemplate";
import SensorActuatorSelectModal from "components/modals/SensorActuatorSelectModal";
import SetDelayModal, {
    defaultDelayValue,
} from "components/modals/SetDelayModal";
import {
    DeviceTypeDict,
    EventConfigLimit,
} from "constant";
import {
    ACTION_FAIL_MESSAGE,
    EVENT_ENABLED_MODIFY_MESSAGE,
    NEW_ACTION_MESSAGE,
} from "constant/messages";
import { Action, Variable } from "generated/models";
import { useCheckIfBasicPlan } from "hooks/useCheckIfBasicPlan";
import { APP_CONFIG } from "config/appConfig";
import { capitalize, find, maxBy, set, sortBy, startCase } from "lodash";
import ActionItem from "components/events/EventDetail/ActionPanel/components/ActionItem";
import DelayWarningAction from "components/events/EventDetail/ActionPanel/components/DelayWarningAction";
import { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { Id } from "react-toastify";
import EventService from "service/eventService";
import LocalStorageService from "service/localStorageService";
import { Event } from "types/Event";
import { Registry } from "types/Gateways";
import { MemberDto } from "types/Organisation";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { isGatewayLevelEvent } from "utils/eventFunctions";
import { isHttpSuccess } from "utils/functions";
import GridStackElement from "utils/gridstack";
import Typography from "@mui/material/Typography";
import { ActuatorCommandMode } from "types/Actuator";

const ACTION_SMS = "sms";
const ACTION_EMAIL = "email";
const ACTION_NOTIF = "notification";
const ACTION_ACTUATOR = "actuator";
const ACTION = "action";
const spacePlaceHolder = "　";

export const getDisplayedActuatorMode = (configMode: any) => {
    let configMode_ = "";

    if (configMode === ActuatorCommandMode.PWM) {
        configMode_ = configMode;
    } else {
        configMode_ = capitalize(configMode);
    }

    return configMode_;
};

export type ActionPanelProps = {
    event: Event;
    actionList: Action[];
    eventVariables: Variable[];
    allGateways: Registry[];
    orgMemberList: MemberDto[];
    showDeleteConfirmation: (type: string, uuid: string) => Id | undefined;
    fetchEvent: () => Promise<void>;
    sortAndUpdateAction: (actions: Action[]) => Promise<void>;
};

export const ActionPanel = ({
    event,
    actionList,
    eventVariables,
    allGateways,
    orgMemberList,
    showDeleteConfirmation,
    fetchEvent,
    sortAndUpdateAction,
}: ActionPanelProps) => {
    const params: any = useParams();
    const metricCLSList = LocalStorageService.getItem("metricInfo").cls;
    const { IS_BASIC_PLAN } = useCheckIfBasicPlan();
    const history = useHistory();
    const isGatewayLevel = isGatewayLevelEvent(event);

    // Disable Send SMS/Email for Basic Plan only when not on-prem
    const shouldDisableSmsEmail =
        (IS_BASIC_PLAN && !APP_CONFIG.APP_IS_ONPREM) || (APP_CONFIG.APP_IS_ONPREM);

    const [selectAction, setSelectAction] = useState(false);
    const [editDelayObj, setEditDelayObj] = useState<{
        delay: number;
        action?: any;
    }>({ delay: defaultDelayValue });
    const [warningPosition, setWarningPosition] = useState<number | null>(null);
    const [selectModalVisible, setSelectModalVisible] = useState(false);
    const [unitType, setUnitType] = useState(DeviceTypeDict.sensor.label);
    const [isShowEventVariables, setIsShowEventVariables] = useState(false);
    const [eventVariableSelected, setEventVariableSelected] = useState("");
    const [errorModal, setErrorModal] = useState({ show: false, content: "" });
    const handleDelayError = (error: string) => {
        setErrorModal({ show: true, content: error });
        setWarningPosition(null);
    };

    const handleActionsClose = () => setSelectAction(false);

    const addNewAction = () => {
        if (event.activate) {
            showErrorAlert({
                message: EVENT_ENABLED_MODIFY_MESSAGE,
            });
            return;
        }
        setSelectAction(true);
    };

    const handleAddDelay = (position: number) => {
        setEditDelayObj({
            delay: Number.isInteger(position) ? defaultDelayValue : 5,
        });
        setWarningPosition(position);
        setSelectModalVisible(true);
        setUnitType("delay");
    };

    const handleCreateDelayAction = async (data: number) => {
        const latestAction: any = maxBy(actionList, "order");
        const max = latestAction?.order ?? 0;
        let previousActionIndex = 0;
        if (warningPosition) {
            previousActionIndex = actionList.findIndex(
                (action) => action.order === warningPosition
            );
        }

        if (
            typeof warningPosition === "number" &&
            !Number.isInteger(warningPosition) &&
            Number(data) < 5
        ) {
            showErrorAlert({
                message: "Delay timer must be at least 5 seconds.",
            });
            setWarningPosition(null);
            return;
        }

        // Calculate the order for the new delay action
        const newOrder = warningPosition
            ? ((actionList[previousActionIndex]?.order ?? 0) +
                warningPosition) /
            2
            : max + 1;

        const response = await EventService.createAction(params.eventId, {
            type_: "DELAY",
            resource: {
                seconds: Number(data),
            },
            order: newOrder,
        });

        if (isHttpSuccess(response.status)) {
            // Create a new action object with the predefined order
            const newAction = {
                ...response.data,
                order: newOrder,
            };

            // Build orders object based on the updated action list
            const updatedActionList = [...actionList, newAction];
            const orders: Record<string, number> = {};
            sortBy(
                updatedActionList.filter(
                    (action) => action.type_ !== "WARNING"
                ),
                "order"
            ).forEach((action, index) => {
                orders[action.uuid] = index;
            });

            // Call sort service to persist the order
            if (orders && Object.keys(orders).length > 0) {
                await EventService.sortOrderAction(params.eventId, orders);
            }

            // Fetch event to update the action list with correct orders from backend
            await fetchEvent();

            showSuccessAlert({
                message: NEW_ACTION_MESSAGE,
            });
        } else {
            showErrorAlert(getAPIError(response, ACTION_FAIL_MESSAGE));
        }
        setWarningPosition(null);
    };

    const handleEditDelayAction = async (data: any) => {
        if (!editDelayObj.action) {
            // Create event delay action
            handleCreateDelayAction(data);
            setEditDelayObj({ delay: defaultDelayValue });
            return;
        }

        if (Number(editDelayObj.delay) === Number(data)) {
            return;
        }

        // Patch event delay action
        const response = await EventService.updateAction(
            params.eventId,
            editDelayObj.action.uuid,
            {
                type_: "DELAY",
                resource: {
                    seconds: Number(data),
                },
            }
        );

        if (!isHttpSuccess(response.status)) {
            setErrorModal({ show: true, content: response.data.description });
            return;
        }

        const actionListCloned = [...actionList];

        const action: Action | undefined = find(actionListCloned, {
            uuid: editDelayObj.action?.uuid,
        });

        if (action) {
            set(action, "resource.seconds", Number(data));
            await sortAndUpdateAction(actionListCloned);

            setEditDelayObj({ delay: defaultDelayValue });
        }
    };

    const handleSelectEventVariable = () => {
        setIsShowEventVariables(false);
        if (!eventVariableSelected) {
            return;
        }
        history.push(
            `/event-details/${params.eventId}/action/event-variable/${eventVariableSelected}`
        );
        setEventVariableSelected("");
    };

    const editDelayModal = (action: any) => {
        setSelectModalVisible(true);
        setUnitType("delay");
        setSelectAction(false);
        setEditDelayObj({
            delay: Number(action.resource?.seconds),
            action,
        });
    };

    const renderActions = () => {
        const actions = actionList.map((action: any) => {
            if (action.type_ === "WARNING") {
                return (
                    <DelayWarningAction
                        key={action.uuid}
                        action={action}
                        handleAddDelay={handleAddDelay}
                    />
                );
            }

            const jsonList = LocalStorageService.getItem("listOfJSON");
            const {
                resource: { gateway_id, device_id, said, variable },
                uuid,
            } = action;
            let sensor: any = {};

            const eventVariableAction: any = eventVariables.find(
                (item: any) => item.uuid === variable
            );

            const deviceOBJ: any = allGateways.reduce((acc: any, g: any) => {
                if (g.gateway_id === gateway_id && g.info) {
                    const deviceList = g.info.devices;
                    let selectedSensor;
                    for (const device of deviceList) {
                        if (device.UID === device_id) {
                            selectedSensor = device;
                            break;
                        }
                    }

                    if (selectedSensor) {
                        acc += selectedSensor.OBJ;
                        sensor = selectedSensor;
                    }
                }
                return acc;
            }, "");

            const getClassAndUnitFromOBJandSAID = (
                obj: string,
                said: string
            ) => {
                let selectedSensor;
                for (const json of jsonList) {
                    if (json.OBJ === obj) {
                        selectedSensor = json;
                        break;
                    }
                }
                const unit: any = selectedSensor?.SNS[said]?.UNIT;
                const cls: any = selectedSensor?.SNS[said]?.CLS;
                const type: string =
                    sensor.APP?.[said]?.NAME ?? metricCLSList?.[cls]?.name;

                return {
                    unit,
                    type,
                };
            };

            const getActionAndConfigMode = allGateways.reduce(
                (acc: any, g: any) => {
                    if (g.gateway_id === gateway_id && g.configs) {
                        const gatewayConfigList = g.configs;
                        let selectedSensor;
                        for (const device of gatewayConfigList) {
                            if (device.UID === device_id) {
                                selectedSensor = device;
                                break;
                            }
                        }

                        if (selectedSensor) {
                            acc.actionMode = selectedSensor.CMDS[said].auto
                                ? ActuatorCommandMode.AUTO
                                : ActuatorCommandMode.MANUAL;
                            acc.configMode = selectedSensor.CMDS[said].mode;
                        }
                    }
                    return acc;
                },
                {}
            );
            const { actionMode = ActuatorCommandMode.MANUAL, configMode } =
                getActionAndConfigMode;

            const sensorName: any = allGateways.reduce((acc: any, g: any) => {
                if (g.gateway_id === gateway_id && g.configs.length > 0) {
                    const sensorList = g.configs;
                    let selectedSensor;
                    for (const sensorItem of sensorList) {
                        if (sensorItem.UID === device_id) {
                            selectedSensor = sensorItem;
                            break;
                        }
                    }

                    if (selectedSensor) {
                        acc += selectedSensor.sensor_names?.[said];
                    }
                }
                return acc;
            }, "");

            return (
                <div
                    className="grid-stack-item"
                    id={`gs-${uuid}`}
                    key={`action-${uuid}`}
                    {...{
                        "gs-w": "12",
                        "gs-no-resize": "true",
                    }}
                >
                    <div className="action-item">
                        <ActionItem
                            actionId={uuid}
                            eventId={params.eventId}
                            showDeleteConfirm={() => {
                                showDeleteConfirmation(ACTION, uuid);
                            }}
                            actuatorName={sensorName}
                            actuatorType={startCase(
                                getClassAndUnitFromOBJandSAID(deviceOBJ, said)
                                    .type || spacePlaceHolder
                            )}
                            actuatorMode={getDisplayedActuatorMode(configMode)}
                            actuatorControl={capitalize(actionMode)}
                            action={action}
                            eventActive={event.activate}
                            editDelayAction={() => {
                                editDelayModal(action);
                            }}
                            eventVariableAction={eventVariableAction}
                        />
                    </div>
                </div>
            );
        });
        return (
            <GridStackElement
                id="actions-list-gridstack"
                itemCount={Number(actionList.length)}
                options={{
                    disableDrag: event.activate,
                }}
                items={actionList}
                onChange={({ event, data }) => {
                    if (event.type === "dragstop" || event.type === "dropped") {
                        const orders: any = {};
                        sortBy(data, "y")
                            .filter((item) => !item.isWarning)
                            .forEach(
                                (item, index) =>
                                (orders[
                                    String(item.id).replace("gs-", "")
                                ] = index)
                            );

                        orders &&
                            EventService.sortOrderAction(
                                params.eventId,
                                orders
                            ).then(async () => {
                                await fetchEvent();
                            });
                    }
                }}
            >
                {actions}
            </GridStackElement>
        );
    };

    const showAction = (actionType: string, config: any = {}) => {
        history.push(`/event-details/${params.eventId}/action/${actionType}`, {
            ...config,
        });
    };

    const showActuatorSelection = () => {
        setSelectModalVisible(true);
        setUnitType(DeviceTypeDict.actuator.label);
        handleActionsClose();
    };

    const showDelayModal = () => {
        setSelectModalVisible(true);
        setUnitType("delay");
        handleActionsClose();
    };

    return (
        <>
            {" "}
            {/* If No actions to list  */}
            {actionList.length ? (
                ""
            ) : (
                <div className="actions-list-empty">
                    <p>Add the actions this event will run.</p>
                    <HoverAuthorizeTooltip permission="event:create">
                        <Button
                            variant="outline-primary"
                            onClick={() => setSelectAction(true)}
                            className="add-condition-action-btn"
                        >
                            + ADD ACTION
                        </Button>
                    </HoverAuthorizeTooltip>
                </div>
            )}
            {/* If actions to list  */}
            <div className="actions-list">
                {renderActions()}

                {actionList.length ? (
                    <Box>
                        <HoverAuthorizeTooltip permission="event:create">
                            <Button
                                variant="primary add-item"
                                onClick={addNewAction}
                                disabled={
                                    isGatewayLevel &&
                                    actionList.length >= EventConfigLimit.ACTIONS_MAX
                                }
                            >
                                +
                            </Button>
                        </HoverAuthorizeTooltip>
                        {isGatewayLevel && (
                            <Typography
                                variant="body1"
                                color="#828FAB"
                                className="mt-2"
                            >
                                Maximum {EventConfigLimit.ACTIONS_MAX} actions
                            </Typography>
                        )}
                    </Box>

                ) : (
                    ""
                )}
            </div>
            {/* Select Action Modal */}
            <Modal
                show={selectAction}
                onHide={handleActionsClose}
                animation={false}
                centered
            >
                <Modal.Header>
                    <Modal.Title>Add Action</Modal.Title>
                    <Button
                        variant=""
                        className="close-button"
                        onClick={handleActionsClose}
                    >
                        <span className="material-icons">close</span>
                    </Button>
                </Modal.Header>

                <Modal.Body>
                    <Form className="condition-options-list">
                        <div className="scroll-list float-left">
                            <Box
                                className={`modal-option ${shouldDisableSmsEmail ? "disabled" : ""
                                    }`}
                                onClick={() => {
                                    if (shouldDisableSmsEmail) return;
                                    showAction(ACTION_EMAIL);
                                }}
                                onKeyDown={() => {
                                    if (shouldDisableSmsEmail) return;
                                    showAction(ACTION_EMAIL);
                                }}
                            >
                                <img src={ActionEmail} alt="action-email" />
                                <div className="float-left">
                                    <h4>Send Email</h4>
                                    <p>
                                        Send email to one or more email
                                        addresses
                                    </p>
                                </div>
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Box>
                            <Box
                                className={`modal-option ${shouldDisableSmsEmail ? "disabled" : ""
                                    }`}
                                onClick={() => {
                                    if (shouldDisableSmsEmail) return;
                                    showAction(ACTION_SMS);
                                }}
                                onKeyDown={() => {
                                    if (shouldDisableSmsEmail) return;
                                    showAction(ACTION_SMS);
                                }}
                            >
                                <img src={ActionSMS} alt="action-sms" />
                                <div className="float-left">
                                    <h4>Send SMS</h4>
                                    <p>Send SMS to one or more phone numbers</p>
                                </div>
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Box>
                            <Box
                                className="modal-option"
                                onClick={() => {
                                    showAction(ACTION_NOTIF);
                                }}
                                onKeyDown={() => {
                                    showAction(ACTION_NOTIF);
                                }}
                            >
                                <img
                                    src={ActionNotification}
                                    alt="action-notification"
                                />
                                <div className="float-left">
                                    <h4>Send Push Notification</h4>
                                    <p>
                                        Send notification to one or more users
                                    </p>
                                </div>
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Box>
                            <Box
                                className="modal-option"
                                onClick={showActuatorSelection}
                                onKeyDown={showActuatorSelection}
                            >
                                <img
                                    src={ActionActuator}
                                    alt="action-actuator"
                                />
                                <div className="float-left">
                                    <h4>Trigger Actuator</h4>
                                    <p>Add an action to trigger Actuator</p>
                                </div>
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Box>
                            <Box
                                className="modal-option"
                                onClick={showDelayModal}
                                onKeyDown={showDelayModal}
                            >
                                <img src={DelayIcon} alt="delay event" />
                                <div className="float-left">
                                    <h4>Add Delay</h4>
                                    <p>
                                        Add a delay before an action gets
                                        triggered
                                    </p>
                                </div>
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Box>

                            <Box
                                className="modal-option"
                                component="fieldset"
                                disabled={isGatewayLevel}
                                onClick={() => {
                                    handleActionsClose();
                                    setIsShowEventVariables(true);
                                }}
                                onKeyDown={() => {
                                    handleActionsClose();
                                    setIsShowEventVariables(true);
                                }}
                            >
                                <img
                                    className="float-left mr-3"
                                    src={evaluateIconBackdrop}
                                    alt="evaluate"
                                />
                                <div className="float-left">
                                    <h4>Modify Event Variable</h4>
                                    <p>Modify the value of Event variable</p>
                                </div>
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Box>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
            {/* Select device Modal */}
            {selectModalVisible && ACTION_ACTUATOR === unitType && (
                <SensorActuatorSelectModal
                    {...{
                        gatewayId: event?.gateway_id,
                        unitType,
                        isVisible: selectModalVisible,
                        onClose: () => {
                            setSelectModalVisible(false);
                        },
                        onFinish: (device: any) => {
                            if (unitType === DeviceTypeDict.actuator.label) {
                                showAction(ACTION_ACTUATOR, {
                                    selectedActuatorDetails: {
                                        name: device.sensorName,
                                        ldsuUUID: device.ldsuUID,
                                        said: device.said,
                                        gatewayId: device.gatewayId,
                                        type: device.sensorType,
                                        obj: device.obj,
                                        appId: device.appId,
                                    },
                                });
                            }
                        },
                    }}
                />
            )}
            {/* Set delay modal */}
            <SetDelayModal
                delay={editDelayObj.delay}
                isOpen={selectModalVisible && unitType === "delay"}
                onClose={() => {
                    setSelectModalVisible(false);
                    setUnitType("");
                    setEditDelayObj({ delay: defaultDelayValue });
                    setWarningPosition(null);
                }}
                onError={handleDelayError}
                onSave={handleEditDelayAction}
                warningPosition={warningPosition}
            />
            <SelectEventVariableModal
                isShowEventVariables={isShowEventVariables}
                setIsShowEventVariables={setIsShowEventVariables}
                eventVariableSelected={eventVariableSelected}
                eventVariables={eventVariables}
                handleSelectedEventVariable={handleSelectEventVariable}
                setEventVariableSelected={setEventVariableSelected}
                modalTitle="Select Event Variable"
            />
            <DefaultModal
                modalShow={errorModal.show}
                setModalShow={(v: boolean) =>
                    setErrorModal({ show: v, content: "" })
                }
                modalType="err"
                modalContent={errorModal.content}
                okAction={() => setErrorModal({ show: false, content: "" })}
            />
        </>
    );
};
